import React from "react";

import {
  alpha,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import CancelIcon from "@mui/icons-material/Cancel";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#9370DB",
  "#3CB371",
  "#DC143C",
  "#4682B4",
];

export const CancelledOrdersStats = ({ data, loading }) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <CancelIcon sx={{ mr: 1, color: "error.main" }} />
          <Typography variant="h6">Pedidos Cancelados</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
            flexDirection: "column",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Não há dados de cancelamentos disponíveis para o período
            selecionado.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Preparar dados de razões de cancelamento para o gráfico
  const reasonChartData = data.reasons.slice(0, 5).map((item, index) => ({
    name:
      item.reason.length > 25
        ? item.reason.substring(0, 25) + "..."
        : item.reason,
    value: item.count,
    percentage: item.percentage,
    color: COLORS[index % COLORS.length],
  }));

  // Preparar dados por canal
  const channelChartData = data.channelSummary
    .slice(0, 5)
    .map((item, index) => ({
      name:
        item.channel.length > 15
          ? item.channel.substring(0, 15) + "..."
          : item.channel,
      value: item.count,
      percentage: item.percentage,
      totalValue: item.value,
      color: COLORS[index % COLORS.length],
    }));

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <CancelIcon sx={{ mr: 1, color: "error.main" }} />
        <Typography variant="h6">Pedidos Cancelados</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: "center", p: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total de Cancelados
            </Typography>
            <Typography variant="h5" sx={{ color: "error.main" }}>
              {data.totalCancelled}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: "center", p: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Taxa de Cancelamento
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: data.rateChange > 0 ? "error.main" : "success.main",
              }}
            >
              {data.cancellationRate.toFixed(1)}%
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: "center", p: 1 }}>
            <Typography variant="body2" color="text.secondary">
              vs. Período Anterior
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: data.rateChange > 0 ? "error.main" : "success.main",
                }}
              >
                {data.rateChange.toFixed(1)}%
              </Typography>
              {data.rateChange > 0 ? (
                <TrendingUpIcon sx={{ ml: 0.5, color: "error.main" }} />
              ) : (
                <TrendingDownIcon sx={{ ml: 0.5, color: "success.main" }} />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Principais Razões de Cancelamento
          </Typography>
          <Box sx={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={reasonChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {reasonChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} pedidos (${props.payload.percentage.toFixed(
                      1
                    )}%)`,
                    props.payload.name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Cancelamentos por Canal
          </Typography>
          <Box sx={{ height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={channelChartData}
                layout="vertical"
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} pedidos (R$${props.payload.totalValue.toFixed(
                      2
                    )})`,
                    props.payload.name,
                  ]}
                />
                <Bar dataKey="value" name="Quantidade">
                  {channelChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Resumo por Tipo de Pedido</strong>
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Quantidade</TableCell>
                  <TableCell align="right">Percentual</TableCell>
                  <TableCell align="right">Valor Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.orderTypeSummary.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {item.type === "DELIVERY"
                        ? "Delivery"
                        : item.type === "TAKEOUT"
                        ? "Retirada"
                        : item.type === "INDOOR"
                        ? "Salão"
                        : item.type}
                    </TableCell>
                    <TableCell align="right">{item.count}</TableCell>
                    <TableCell align="right">
                      {item.percentage.toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">
                      R${item.value.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              p: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" gutterBottom>
              <strong>Insights sobre cancelamentos:</strong>
            </Typography>
            <Typography variant="body2" component="div">
              <ul style={{ margin: 0, paddingLeft: "1.5rem" }}>
                {data.cancellationRate > data.prevCancellationRate && (
                  <li>
                    Aumento de{" "}
                    {(
                      data.cancellationRate - data.prevCancellationRate
                    ).toFixed(1)}
                    % na taxa de cancelamento comparado ao período anterior.
                  </li>
                )}
                {data.cancellationRate < data.prevCancellationRate && (
                  <li>
                    Redução de{" "}
                    {(
                      data.prevCancellationRate - data.cancellationRate
                    ).toFixed(1)}
                    % na taxa de cancelamento comparado ao período anterior.
                  </li>
                )}
                {data.reasons.length > 0 && (
                  <li>
                    A principal razão de cancelamento foi "
                    {data.reasons[0].reason}", responsável por{" "}
                    {data.reasons[0].percentage.toFixed(1)}% dos cancelamentos.
                  </li>
                )}
                {data.channelSummary.length > 0 && (
                  <li>
                    O canal com mais cancelamentos foi "
                    {data.channelSummary[0].channel}" com{" "}
                    {data.channelSummary[0].count} pedidos (
                    {data.channelSummary[0].percentage.toFixed(1)}%).
                  </li>
                )}
                {data.totalValue > 0 && (
                  <li>
                    O valor total dos pedidos cancelados foi de R$
                    {data.totalValue.toFixed(2)}.
                  </li>
                )}
              </ul>
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
